import React from 'react';
import { useIntl } from 'react-intl';
import { IPartner, IUser } from '@whitelabel/helpers/types';
import { SHOPEE_PARTNER_ID } from '@whitelabel/xcover-shared/helpers/constants';
import { ReactComponent as Print } from '@whitelabel/media/icons/small/print.svg';
import { POLICY_TYPE } from '@whitelabel/helpers/constants';
import { getPolicyTitle } from '../../../helpers/bookings';
import { renderDisclaimerContent } from '../../../helpers/utils';
import { IPDSPolicy } from '../../../helpers/types';
import messages from './messages';
import {
  StyledPartnerHeading,
  StyledPolicyHeading,
  StyledPolicyTabs,
  StyledDisclaimer,
  StyledButton,
  StyledWrapper,
} from './styledPDSHeader';

export interface IPDSHeaderProps {
  user?: IUser;
  partner?: IPartner;
  policies?: IPDSPolicy[];
  activePolicy?: string;
  setActivePolicy: (...args: any[]) => any;
  className?: string;
}

const PDSHeader = ({
  user,
  partner,
  policies,
  activePolicy,
  setActivePolicy,
  className,
}: IPDSHeaderProps): JSX.Element => {
  const intl = useIntl();
  const tabs = policies
    ? policies.map((policy: any) => ({
        policyVersion: policy.policyVersion,
        policyType: policy.policyType,
        policyTitle: getPolicyTitle(policy),
      }))
    : null;
  const showDisclaimer = SHOPEE_PARTNER_ID === partner?.id;
  const hasCOVIDPolicy = policies?.some((p: any) => p.policyType === POLICY_TYPE.TRAVEL_COVID);

  const renderPrintButton = () => (
    <StyledButton
      icon={Print}
      buttonType="tertiary"
      data-test-id="PDSHeader-printWording"
      data-analytics="PDSPrintWording"
      onClick={() => window.print()}
      className="hide-on-print"
    >
      {intl.formatMessage(messages.printWording)}
    </StyledButton>
  );
  return (
    <StyledWrapper className={className}>
      {policies &&
        (policies.length === 1 ? (
          <StyledPartnerHeading
            hasCOVIDPolicy={hasCOVIDPolicy}
            singlePolicy
            partner={user ? partner : undefined}
            showDisclaimer={showDisclaimer}
          >
            <StyledPolicyHeading singlePolicy {...tabs?.[0]} />
            {showDisclaimer && (
              <StyledDisclaimer>
                {renderDisclaimerContent(policies[0].content.disclaimerHtml || policies[0].content.disclaimer)}
              </StyledDisclaimer>
            )}
            {renderPrintButton()}
          </StyledPartnerHeading>
        ) : (
          <StyledPartnerHeading
            title={intl.formatMessage(messages.multiPolicyHeading)}
            partner={user ? partner : undefined}
            hasCOVIDPolicy={hasCOVIDPolicy}
            titleSubcontent={renderPrintButton()}
            titleTag="h1"
          >
            <StyledPolicyTabs tabs={tabs} activeTab={activePolicy} onClick={setActivePolicy} />
          </StyledPartnerHeading>
        ))}
    </StyledWrapper>
  );
};

PDSHeader.defaultProps = {
  user: null,
  partner: null,
  policies: null,
  activePolicy: null,
};

export default PDSHeader;
